import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { QrCode, Download, ExternalLink, Copy, Check, FileImage, FileCode, FileText } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import api from '../../lib/api';

/**
 * QR code section for agent or agency profile.
 * Shows live preview + download buttons (PNG/SVG/PDF) + copy public URL.
 *
 * Props:
 *   kind: 'agent' | 'agency'
 */
export default function QRCodeSection({ kind = 'agent' }) {
    const [info, setInfo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [pngBlobUrl, setPngBlobUrl] = useState(null);
    const [copied, setCopied] = useState(false);
    const [downloading, setDownloading] = useState(null);

    const loadInfo = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.get('/qr/info/me');
            setInfo(r.data);
        } catch (e) {
            toast.error('Erreur lors du chargement du QR code');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { loadInfo(); }, [loadInfo]);

    // Live PNG preview (blob URL so we can show <img>)
    useEffect(() => {
        let canceled = false;
        let createdUrl = null;
        const load = async () => {
            try {
                const r = await api.get(`/qr/${kind}/me?fmt=png`, { responseType: 'blob' });
                if (canceled) return;
                createdUrl = URL.createObjectURL(r.data);
                setPngBlobUrl(createdUrl);
            } catch (e) {
                // 403 for agent on /agency or vice versa — silently ignore
            }
        };
        if (info) load();
        return () => {
            canceled = true;
            if (createdUrl) URL.revokeObjectURL(createdUrl);
        };
    }, [info, kind]);

    const downloadFormat = async (fmt) => {
        setDownloading(fmt);
        try {
            const r = await api.get(`/qr/${kind}/me?fmt=${fmt}`, { responseType: 'blob' });
            const blob = r.data;
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            const slug = (kind === 'agent' ? info?.agent?.slug : info?.agency?.slug) || 'homly';
            a.download = `homly-qr-${slug}.${fmt}`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 5000);
            toast.success(`QR code ${fmt.toUpperCase()} téléchargé`);
        } catch (e) {
            toast.error(`Erreur téléchargement ${fmt.toUpperCase()}`);
        } finally {
            setDownloading(null);
        }
    };

    const target = kind === 'agent' ? info?.agent : info?.agency;
    const url = target?.url;

    const copyLink = async () => {
        if (!url) return;
        try {
            await navigator.clipboard.writeText(url);
            setCopied(true);
            toast.success('Lien copié dans le presse-papier');
            setTimeout(() => setCopied(false), 2000);
        } catch {
            toast.error('Impossible de copier');
        }
    };

    if (loading) {
        return <div className="h-64 rounded-2xl bg-card border border-border animate-pulse" data-testid="qr-loading" />;
    }
    if (!target) {
        return null; // agency-kind for an agent → nothing shown
    }

    return (
        <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-card backdrop-blur-sm rounded-2xl border border-border overflow-hidden"
            data-testid={`qr-card-${kind}`}
        >
            <div className="p-6 border-b border-border">
                <h2 className="text-lg font-bold text-foreground flex items-center gap-2">
                    <QrCode className="w-5 h-5 text-[#681793]" />
                    QR Code {kind === 'agent' ? 'agent' : 'agence'}
                </h2>
                <p className="text-sm text-muted-foreground mt-1">
                    À imprimer sur vos cartes de visite, vitrines, signatures email. Les scans renvoient directement vers votre profil public.
                </p>
            </div>
            <div className="p-6 grid md:grid-cols-2 gap-6 items-center">
                {/* Preview */}
                <div className="flex justify-center min-w-0">
                    <div className="p-4 bg-white rounded-2xl shadow-2xl shadow-[#681793]/10" data-testid={`qr-preview-${kind}`}>
                        {pngBlobUrl ? (
                            <img src={pngBlobUrl} alt={`QR ${target.name}`} className="w-48 h-48 md:w-56 md:h-56" />
                        ) : (
                            <div className="w-48 h-48 md:w-56 md:h-56 flex items-center justify-center text-slate-300">
                                <QrCode className="w-20 h-20 animate-pulse" />
                            </div>
                        )}
                    </div>
                </div>

                {/* Actions */}
                <div className="space-y-4 min-w-0">
                    <div>
                        <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Lien public</p>
                        <div className="flex items-center gap-2 p-3 rounded-xl bg-background border border-border">
                            <span className="flex-1 min-w-0 text-sm text-foreground truncate" data-testid={`qr-url-${kind}`}>
                                {url}
                            </span>
                            <Button
                                size="sm"
                                variant="ghost"
                                onClick={copyLink}
                                data-testid={`qr-copy-${kind}`}
                                className="shrink-0"
                            >
                                {copied ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                            </Button>
                            <a
                                href={url}
                                target="_blank"
                                rel="noreferrer"
                                className="p-2 rounded-lg hover:bg-card transition-colors"
                                data-testid={`qr-open-${kind}`}
                            >
                                <ExternalLink className="w-4 h-4 text-muted-foreground" />
                            </a>
                        </div>
                    </div>

                    <div>
                        <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Télécharger</p>
                        <div className="grid grid-cols-3 gap-2">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => downloadFormat('png')}
                                disabled={!!downloading}
                                className="h-auto py-3 flex-col gap-1 border-border"
                                data-testid={`qr-download-png-${kind}`}
                            >
                                <FileImage className="w-4 h-4" />
                                <span className="text-xs font-semibold">PNG</span>
                                <span className="text-[10px] text-muted-foreground">1024px HD</span>
                            </Button>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => downloadFormat('svg')}
                                disabled={!!downloading}
                                className="h-auto py-3 flex-col gap-1 border-border"
                                data-testid={`qr-download-svg-${kind}`}
                            >
                                <FileCode className="w-4 h-4" />
                                <span className="text-xs font-semibold">SVG</span>
                                <span className="text-[10px] text-muted-foreground">Vectoriel</span>
                            </Button>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => downloadFormat('pdf')}
                                disabled={!!downloading}
                                className="h-auto py-3 flex-col gap-1 border-border"
                                data-testid={`qr-download-pdf-${kind}`}
                            >
                                <FileText className="w-4 h-4" />
                                <span className="text-xs font-semibold">PDF</span>
                                <span className="text-[10px] text-muted-foreground">A6 imprimable</span>
                            </Button>
                        </div>
                    </div>

                    <Button
                        onClick={() => downloadFormat('png')}
                        disabled={!!downloading}
                        className="w-full bg-gradient-to-r from-[#681793] to-[#FF914D] hover:opacity-90 text-white"
                        data-testid={`qr-download-default-${kind}`}
                    >
                        <Download className="w-4 h-4 mr-2" />
                        {downloading ? 'Téléchargement…' : 'Télécharger le QR (PNG HD)'}
                    </Button>
                </div>
            </div>
        </motion.div>
    );
}
